/**
 * Auth store — session state for the SmartDash SPA (ADR-003 D8 dual login).
 *
 * Two responsibilities:
 *   1. Hold the per-session identity + status + auth kind for the AuthGate
 *      and the AppHeader to render.
 *   2. Drive the login/logout flow with bounded, named state — every
 *      transition is observable; failures carry the pre-classified reason
 *      so the LoginPage is a dumb renderer (per ADR-003 §1.2 QAS-2).
 *
 * Two auth surfaces, one gate:
 *   - `local`    customer via the gateway (/api/auth/{login, me, logout})
 *   - `employee` chrilantech staff via the BFF (/api/staff-auth/{login, verify, me, logout})
 *
 * Hydration probes /api/auth/me first; on 401 it then probes /api/staff-auth/me.
 * The first surface that returns 200 wins. Both surfaces use the same
 * session-cookie + AuthUser shape so the dashboard doesn't distinguish.
 *
 * 401 side effects: apiClient.setUnauthorizedHandler() is registered at module
 * init so that ANY backend 401 (login aside) flips the store to 'anonymous'
 * and re-renders the AuthGate. The session cookie being missing/expired is
 * the only signal that means "show login again".
 */

import { create } from 'zustand'
import { login as customerLogin, logout as customerLogout, me as customerMe } from '@api/authApi'
import {
  login as employeeLogin,
  verify as employeeVerify,
  logout as employeeLogout,
  me as employeeMe,
} from '@api/staffAuthApi'
import { setUnauthorizedHandler } from '@api/apiClient'
import { ApiError } from '@domain/types'
import { type CustomerGatewayCapability, probeCustomerGateway } from '@lib/capabilityProbe'
import {
  AuthApiError,
  type AuthKind,
  type AuthStatus,
  type AuthUser,
  type LoginFailureReason,
} from '@domain/auth'

/**
 * Step the LoginPage is on for the employee flow. The customer flow never
 * reaches `verify` (it returns 200 with the session cookie directly).
 */
export type EmployeeStep = 'credentials' | 'otp'

interface AuthState {
  readonly status: AuthStatus
  readonly authKind: AuthKind | null
  readonly currentUser: AuthUser | null
  readonly lastFailureReason: LoginFailureReason | null
  readonly version: number
  /** Employee OTP flow state — only set while authKind === 'employee' and not yet verified. */
  readonly pendingLoginRef: string | null
  readonly pendingDisplayName: string | null
  readonly employeeStep: EmployeeStep
  /**
   * Runtime detection of the customer auth gateway on the current host
   * (SIH-17 / DEF-B4-1 / PM Option C). Resolved during hydrate() by probing
   * /api/auth/healthz. The LoginPage only renders the Customer tab when
   * capability is `present`; the auth store refuses to call customerLogin
   * when capability is anything other than `present`.
   *
   * `unknown` is the initial state and is shown as the loading frame by
   * the AuthGate until the probe completes.
   */
  readonly customerGatewayAvailable: CustomerGatewayCapability

  /** Probe both auth surfaces on app mount. Idempotent; safe to re-call. */
  hydrate: (signal?: AbortSignal) => Promise<void>
  /** POST /api/auth/login with the typed credentials (customer flow). */
  customerLogin: (username: string, password: string, signal?: AbortSignal) => Promise<AuthUser>
  /**
   * Step 1 of the employee flow: POST /api/staff-auth/login → store the
   * opaque loginRef and advance to the OTP step.
   */
  employeeLogin: (username: string, password: string, signal?: AbortSignal) => Promise<{ displayName: string; message: string }>
  /** Step 2 of the employee flow: POST /api/staff-auth/verify with the OTP. */
  employeeVerify: (otp: string, signal?: AbortSignal) => Promise<AuthUser>
  /** Back out of the OTP step (user clicked "use different credentials"). */
  cancelEmployeeLogin: () => void
  /** POST the matching /logout endpoint + clear local state. Never throws. */
  logout: (signal?: AbortSignal) => Promise<void>
  /** Internal — called by apiClient.setUnauthorizedHandler when any backend 401 lands. */
  markSessionExpired: () => void
}

/**
 * The session fields an aborted attempt restores.
 *
 * An abort is a CANCELLATION, not a failure: the caller withdrew the request
 * (the component unmounted, the operator navigated away), so the attempt is
 * undone and the store returns to the state it was in before the attempt
 * started. Leaving the store on 'authenticating' or 'hydrating' after an abort
 * is a defect - nothing is in flight, but the LoginPage's isSubmitting flag
 * would stay true and the operator could not retry. It is the same defect
 * class as the SIH-13 OTP fix (see employeeLogin).
 *
 * `customerGatewayAvailable` is deliberately NOT part of the snapshot: the
 * capability probe is what establishes it, and an aborted probe establishes
 * nothing. The capability stays 'unknown' - the safe verdict, which refuses a
 * customer sign-in rather than authorising one on a stale reading.
 */
type SessionSnapshot = Pick<
  AuthState,
  | 'status'
  | 'authKind'
  | 'currentUser'
  | 'lastFailureReason'
  | 'pendingLoginRef'
  | 'pendingDisplayName'
  | 'employeeStep'
>

const snapshotSession = (state: AuthState): SessionSnapshot => ({
  status: state.status,
  authKind: state.authKind,
  currentUser: state.currentUser,
  lastFailureReason: state.lastFailureReason,
  pendingLoginRef: state.pendingLoginRef,
  pendingDisplayName: state.pendingDisplayName,
  employeeStep: state.employeeStep,
})

const INITIAL_STATE: Pick<AuthState, 'status' | 'authKind' | 'currentUser' | 'version' | 'employeeStep' | 'pendingLoginRef' | 'pendingDisplayName' | 'customerGatewayAvailable'> = {
  status: 'unknown',
  authKind: null,
  currentUser: null,
  employeeStep: 'credentials',
  pendingLoginRef: null,
  pendingDisplayName: null,
  customerGatewayAvailable: 'unknown',
  // bump version on every transition so React effects can subscribe to it
  // without re-running on every status change (status alone is the
  // authoritative signal; version is a cheap "something changed" hint).
  version: 0,
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...INITIAL_STATE,
  lastFailureReason: null,

  hydrate: async (signal) => {
    const current = get().status
    if (current === 'authenticated' || current === 'authenticating' || current === 'awaiting_otp') return
    const before = snapshotSession(get())
    set({ status: 'hydrating', customerGatewayAvailable: 'unknown', version: get().version + 1 })
    // Capability probe runs in parallel with the session probes. The probe
    // is a single GET to /api/auth/healthz; same-origin; never crosses the
    // domain. The probe collapses every failure mode (404, 5xx, timeout,
    // network) to 'absent' so the hydrate flow never throws because of it.
    const capabilityPromise = probeCustomerGateway(signal)
    try {
      // Probe the customer surface first; only fall back to staff on 401.
      // (The order matches the LoginPage's default tab — the operator chose
      // customer as the primary surface because customer hosts outnumber
      // operator sessions.)
      const customer = await customerMe(signal)
      const customerGatewayAvailable: CustomerGatewayCapability = await capabilityPromise
      if (customer !== null) {
        set({
          status: 'authenticated',
          authKind: 'local',
          currentUser: customer,
          customerGatewayAvailable,
          version: get().version + 1,
        })
        return
      }
      const employee = await employeeMe(signal)
      if (employee !== null) {
        set({
          status: 'authenticated',
          authKind: 'employee',
          currentUser: employee,
          customerGatewayAvailable,
          version: get().version + 1,
        })
        return
      }
      set({
        status: 'anonymous',
        authKind: null,
        currentUser: null,
        customerGatewayAvailable,
        version: get().version + 1,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Cancelled, not failed: return to the state the store was in before
        // this attempt, so nothing is left stuck on 'hydrating'. The
        // capability stays 'unknown' - the aborted probe established nothing.
        set({ ...before, version: get().version + 1 })
        return
      }
      // Both surfaces unreachable / 5xx — keep the user on the loading frame
      // rather than flashing a misleading login screen. QAS-4: named error
      // state, never silent.
      set({ status: 'unknown', version: get().version + 1 })
    }
  },

  customerLogin: async (username, password, signal) => {
    // Capability gate (SIH-17 / DEF-B4-1 / PM Option C). Customer login is
    // only valid where the customer auth gateway is verified. On hosts
    // without the gateway (ncv parked, future hosts not yet configured),
    // customer login must throw a typed error — never a fake network
    // call, never a placeholder success. The LoginPage never renders the
    // Customer tab in this state, so this branch is the deep-link guard
    // for any code path that tries to bypass the UI.
    const capability = get().customerGatewayAvailable
    if (capability !== 'present') {
      throw new ApiError(
        0,
        'CUSTOMER_GATEWAY_UNAVAILABLE',
        'Customer login is not available on this host.',
      )
    }
    const before = snapshotSession(get())
    set({
      status: 'authenticating',
      authKind: 'local',
      pendingLoginRef: null,
      pendingDisplayName: null,
      employeeStep: 'credentials',
      version: get().version + 1,
    })
    try {
      const result = await customerLogin(username, password, signal)
      set({
        status: 'authenticated',
        authKind: 'local',
        currentUser: result.user,
        lastFailureReason: null,
        version: get().version + 1,
      })
      return result.user
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Cancelled, not failed: return to the state the store was in before
        // the attempt, so nothing is left stuck on 'authenticating' and the
        // operator can try again.
        set({ ...before, version: get().version + 1 })
        throw error
      }
      // AuthApiError carries the pre-classified reason; anything else is
      // rewrapped by authApi.login() so we never see a raw ApiError here.
      const reason = error instanceof AuthApiError ? error.reason : 'unavailable'
      set({
        status: 'failed',
        authKind: 'local',
        currentUser: null,
        lastFailureReason: reason,
        version: get().version + 1,
      })
      throw error
    }
  },

  employeeLogin: async (username, password, signal) => {
    const before = snapshotSession(get())
    set({
      status: 'authenticating',
      authKind: 'employee',
      employeeStep: 'credentials',
      pendingLoginRef: null,
      pendingDisplayName: null,
      version: get().version + 1,
    })
    try {
      const result = await employeeLogin(username, password, signal)
      // The BFF accepted the credentials but wants an OTP. Hold the opaque
      // loginRef + the prompt message; the SPA's employee OTP step renders
      // an input that calls employeeVerify() next.
      //
      // CRITICAL: status flips out of `authenticating` here. `authenticating`
      // means "a network call is in flight" — by definition nothing is in
      // flight right now, the BFF already answered 202 and we're waiting on
      // the user. Leaving status at `authenticating` made the LoginPage's
      // `isSubmitting` flag stick, which disabled the OTP input, which is
      // why the e2e `login-otp` fill timed out (see SIH-13 fix).
      set({
        status: 'awaiting_otp',
        authKind: 'employee',
        employeeStep: 'otp',
        pendingLoginRef: result.loginRef,
        pendingDisplayName: result.displayName,
        lastFailureReason: null,
        version: get().version + 1,
      })
      return { displayName: result.displayName, message: result.message }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Cancelled, not failed: return to the state the store was in before
        // the attempt, so nothing is left stuck on 'authenticating'.
        set({ ...before, version: get().version + 1 })
        throw error
      }
      const reason = error instanceof AuthApiError ? error.reason : 'unavailable'
      set({
        status: 'failed',
        authKind: 'employee',
        currentUser: null,
        lastFailureReason: reason,
        version: get().version + 1,
      })
      throw error
    }
  },

  employeeVerify: async (otp: string, signal?: AbortSignal): Promise<AuthUser> => {
    const loginRef = get().pendingLoginRef
    if (loginRef === null) {
      throw new ApiError(0, 'NO_PENDING_LOGIN', 'No pending employee login to verify.')
    }
    const before = snapshotSession(get())
    set({ status: 'authenticating', version: get().version + 1 })
    try {
      const user: AuthUser = await employeeVerify(loginRef, otp, signal)
      set({
        status: 'authenticated',
        authKind: 'employee',
        currentUser: user,
        pendingLoginRef: null,
        employeeStep: 'credentials',
        lastFailureReason: null,
        version: get().version + 1,
      })
      return user
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Cancelled, not failed: return to the state the store was in before
        // the attempt - the OTP the operator was typing is still pending, so
        // the OTP step stays live and they can submit it again.
        set({ ...before, version: get().version + 1 })
        throw error
      }
      const reason = error instanceof AuthApiError ? error.reason : 'unavailable'
      // TL Ruling 4: the BFF consumes the sign-in reference before it asks the provider about the code,
      // so after ANY failure reached from the verify step - a rejected code, a refused attempt, an
      // unnamed failure, too many attempts, an unreachable service - the reference is spent or of
      // unknown state. The user goes back to step 1; the reference is dropped, never retried, and
      // nothing is re-submitted for them.
      set({
        status: 'failed',
        authKind: 'employee',
        currentUser: null,
        pendingLoginRef: null,
        pendingDisplayName: null,
        employeeStep: 'credentials',
        lastFailureReason: reason,
        version: get().version + 1,
      })
      throw error
    }
  },

  cancelEmployeeLogin: () => {
    // User backed out of the OTP step — go back to the credentials form.
    // Clear authKind too so the LoginPage renders neutral (neither tab is
    // "active-by-stale-attempt"); the user picks Customer or Employee fresh.
    set({
      authKind: null,
      employeeStep: 'credentials',
      pendingLoginRef: null,
      pendingDisplayName: null,
      status: 'anonymous',
      lastFailureReason: null,
      version: get().version + 1,
    })
  },

  logout: async (signal) => {
    // Always clear local state — even if the network call fails. QAS-3
    // contract: logout is non-blocking for the user; the session expires
    // server-side regardless. The right endpoint is chosen by authKind so
    // we revoke exactly the session that owns the cookie (the customer
    // session cookie is independent from the staff session cookie).
    const kind = get().authKind
    try {
      if (kind === 'employee') {
        await employeeLogout(signal)
      } else {
        await customerLogout(signal)
      }
    } finally {
      set({
        status: 'anonymous',
        authKind: null,
        currentUser: null,
        pendingLoginRef: null,
        pendingDisplayName: null,
        employeeStep: 'credentials',
        lastFailureReason: null,
        version: get().version + 1,
      })
    }
  },

  markSessionExpired: () => {
    // apiClient calls this on any 401 from a backend call. Don't downshift
    // when the user is mid-login (the login call itself may have produced a
    // 401; that's expected and the LoginPage is already rendering it).
    // `awaiting_otp` is also mid-login: an unrelated 401 should not blow away
    // the OTP the user has been typing into.
    if (get().status === 'authenticating') return
    if (get().status === 'awaiting_otp') return
    if (get().status === 'anonymous') return
    set({
      status: 'anonymous',
      authKind: null,
      currentUser: null,
      pendingLoginRef: null,
      pendingDisplayName: null,
      employeeStep: 'credentials',
      lastFailureReason: null,
      version: get().version + 1,
    })
  },
}))

/** Wire apiClient's 401 side-channel to the store exactly once. */
setUnauthorizedHandler(() => {
  useAuthStore.getState().markSessionExpired()
})

/** Convenience selector for components that only care about the user. */
export function selectCurrentUser(state: AuthState): AuthUser | null {
  return state.currentUser
}

/** Convenience selector for the AuthGate — true when dashboard is allowed. */
export function selectIsAuthenticated(state: AuthState): boolean {
  return state.status === 'authenticated' && state.currentUser !== null
}
